import useModal from '@area2k/use-modal';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { differenceInHours, parseISO } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { getTaxTypeLabel } from '../Order/util';

import DetailsCard from './DetailsCard';
import JobBadge from './JobBadge';
import JobHeader from './JobHeader';
import PageSkeleton from './PageSkeleton';
import WorkerTable from './WorkerTable';
import CancellationDissmissModal from './modals/CancellationAndDismissModal';
import ConfirmModalCancellation from './modals/ConfirmModalCancellation';
import { getJobStatusByJob, JobStatus } from './util';

import Alert from '@/components/Alert';
import Badge from '@/components/Badge';
import Page from '@/components/Page';
import PreferredWorkers from '@/components/PreferredWorkers';
import Stack from '@/components/Stack';
import Tabs, { TabDefinition } from '@/components/Tabs';
import { GAEvent } from '@/constants/gaevents';
import { TIME_TO_REFRESH } from '@/constants/general';
import {
  ContextCancelRemoveModal,
  ContextConfirmModalCancellation,
} from '@/constants/job';
import {
  useGetJobQuery,
  useUnPublishJobMutation,
  usePublishJobMutation,
} from '@/graphql';
import useAuth from '@/hooks/useAuth';
import useNullableMediaQuery from '@/hooks/useNullableMediaQuery';
import styled from '@/styles';
import { GetJobQuery, JobTypeEnum } from '@/types/graphql';
import { JobsAPI, JobQualityGateData, JobSupplyInfo } from '@/services/jobsAPI';
import { PreferredPoolsAPI } from '@/services/preferredPoolsAPI';
import useAnalytics from '@/util/analytics';
import { DAY_HOURS } from '@/util/constants';
import { formatISO, isWithin24Hours } from '@/util/date';
import { getHiredWorkersByJobWorkers } from '@/util/job';
import AgencyChat from '@/routes/Agency/Chat';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { isIOS } from '@/util/platform';

const JobDetailsWrapper = styled('div', {
  display: 'flex',
  flexFlow: 'row wrap',
  justifyContent: 'flex-start',
  gap: '50px',
  padding: '20px',
  '@phoneOnly': {
    justifyContent: 'center',
    gap: 0,
    padding: 0,
  },
});
const JobRevamp = () => {
  const { jobId, orderId } = useParams();
  const { currentAdminIsCustomerAdmin } = useAuth();
  const query = useGetJobQuery({
    variables: { jobId },
    pollInterval: TIME_TO_REFRESH * 2,
  });
  const [tab, setTab] = useState(0);
  const [qualityGate, setQualityGate] = useState<JobQualityGateData>({
    quality_gate_disabled: false,
    quality_gate_disabled_reason: null,
    quality_gate_disabled_at: null,
    customer_quality_gate_enabled: false,
  });
  const [supplyInfo, setSupplyInfo] = useState<JobSupplyInfo | null>(null);
  // Preferred Workers tab (GRAVY-10265 F9): server-driven gate — the tab only
  // exists once the pools list endpoint confirms the feature is on (P13), and
  // never for customer admins (P10).
  const [poolsEnabled, setPoolsEnabled] = useState(false);
  const phoneOnly = useNullableMediaQuery('(max-width: 559px)', null);
  const navigate = useNavigate();

  const { logEvent } = useAnalytics();

  const fetchQualityGate = useCallback(() => {
    if (jobId) {
      JobsAPI.getQualityGateData(jobId)
        .then(setQualityGate)
        .catch(() => {});
      JobsAPI.getQualityGateActiveJobIds([Number(jobId)])
        .then(({ supplyInfo: info }) => {
          setSupplyInfo(info[Number(jobId)] ?? null);
        })
        .catch(() => {});
    }
  }, [jobId]);

  useEffect(() => {
    logEvent(GAEvent.ViewJobDetails, jobId);
  }, []);

  useEffect(() => {
    fetchQualityGate();
  }, [fetchQualityGate]);

  const jobCustomerId = query?.data?.job?.account?.customer?.id;
  const jobSkillId = query?.data?.job?.skill?.id;

  useEffect(() => {
    if (!jobCustomerId || currentAdminIsCustomerAdmin) return;
    PreferredPoolsAPI.listPools(jobCustomerId)
      .then((result) => setPoolsEnabled(result.enabled))
      .catch(() => setPoolsEnabled(false));
  }, [jobCustomerId, currentAdminIsCustomerAdmin]);

  const job = query?.data?.job || {};

  const [showCancelJobModal, hideCancelJobModal] = useModal(
    () => (
      <CancellationDissmissModal
        context={ContextCancelRemoveModal.CancelJob}
        hideModal={() => {
          hideCancelJobModal();
          hideConfirmCancelJob();
        }}
        job={job}
      />
    ),
    [job],
  );

  const [confirmCancelJob, hideConfirmCancelJob] = useModal(
    () => (
      <ConfirmModalCancellation
        context={ContextConfirmModalCancellation.CancelJob}
        hideModal={hideConfirmCancelJob}
        onAction={() => {
          showCancelJobModal({ job });
        }}
      />
    ),
    [job],
  );

  const onCancelPress = (job: GetJobQuery['job']) => {
    const firstShiftStartAt = parseISO(job.firstShiftStartAt);

    const differenceHours = differenceInHours(firstShiftStartAt, new Date());

    if (differenceHours < DAY_HOURS && currentAdminIsCustomerAdmin) {
      return confirmCancelJob({ job });
    }

    return showCancelJobModal({ job });
  };

  const [jobPublish] = usePublishJobMutation({
    update: (cache) => {
      cache.modify({
        id: cache.identify(job),
        fields: { job() {} },
      });
    },
  });
  const [jobUnpublish] = useUnPublishJobMutation({
    update: (cache) => {
      cache.modify({
        id: cache.identify(job),
        fields: { job() {} },
      });
    },
  });

  const togglePublish = async () => {
    const value = jobPublished;
    try {
      if (value) {
        return await jobUnpublish({ variables: { jobId } });
      }

      return await jobPublish({ variables: { jobId } });
    } catch (err) {
      return err;
    }
  };

  if (!query.data) {
    return <PageSkeleton />;
  }

  const jobStatus = getJobStatusByJob(job);
  const jobPublished = job.published;
  const jobWorkersCount = getHiredWorkersByJobWorkers(job.jobWorkers);
  const TABS: TabDefinition[] = [
    {
      a11yLabel: 'Workers',
      label: 'Workers',
      id: `${jobWorkersCount.length}/${job.quantity}`,
    },
    { a11yLabel: 'Job Details', label: 'Job Details' },
    ...(poolsEnabled && !currentAdminIsCustomerAdmin
      ? [{ a11yLabel: 'Preferred Workers', label: 'Preferred Workers' }]
      : []),
  ];

  const getJobStatusLabel = () => {
    const startAtIso = parseISO(job.shifts[0].startAt);
    if (isWithin24Hours(startAtIso)) {
      if (!jobPublished && jobStatus === JobStatus.CANCELLED) {
        return [JobStatus.UNPUBLISHED, jobStatus];
      } else if (!jobPublished) {
        return JobStatus.UNPUBLISHED;
      } else if (jobStatus === JobStatus.CANCELLED) {
        return jobStatus;
      } else {
        return [];
      }
    } else if (!jobPublished) {
      return [JobStatus.UNPUBLISHED, jobStatus];
    } else {
      return jobStatus;
    }
  };

  const displayLabels = () => {
    const LABELS = [
      <JobBadge key={'status'} jobStatus={getJobStatusLabel()} />,
    ];
    if (job.jobType === JobTypeEnum.LTA) {
      LABELS.push(
        <Badge
          key={'lta'}
          css={{ marginRight: '10px' }}
          label="LTA"
          status={'themeDefault'}
        />,
      );
    }

    if (
      !currentAdminIsCustomerAdmin &&
      qualityGate.customer_quality_gate_enabled &&
      !qualityGate.quality_gate_disabled
    ) {
      LABELS.push(
        <span
          key={'quality-gate'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: '#E7F4E680',
            borderRadius: '100px',
            padding: '4px 8px',
            marginRight: '10px',
            fontFamily: 'Manrope',
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '140%',
            color: '#44A735',
            whiteSpace: 'nowrap',
          }}
        >
          {supplyInfo?.low_supply
            ? 'Dependability Gate: Relaxed'
            : 'Dependability Gate: Strict'}
        </span>,
      );
    }

    return LABELS;
  };

  const workerDetails = () => {
    return (
      <Stack
        vertical
        gap={10}
        style={phoneOnly ? { padding: '0px' } : { padding: '0px 20px' }}
      >
        <WorkerTable
          job={job}
          jobStatus={jobStatus}
          refetchJob={() => query.refetch()}
          qualityGateActive={
            qualityGate.customer_quality_gate_enabled &&
            !qualityGate.quality_gate_disabled
          }
        />
      </Stack>
    );
  };
  const jobDetails = () => {
    return (
      <JobDetailsWrapper>
        <DetailsCard
          job={job}
          refetchJob={() => query.refetch()}
          qualityGate={qualityGate}
          onQualityGateUpdated={fetchQualityGate}
        />
      </JobDetailsWrapper>
    );
  };

  const chatIntercomIcon = useFeatureValue(
    FEATURE_TOGGLE.ChatIntercomIcon,
    false,
  );
  const ChatIntercomIconIOS = useFeatureValue(
    FEATURE_TOGGLE.ChatIntercomIconIOS,
    false,
  );

  return (
    <Page
      noPadding
      contentVarient="true"
      headerPadding={phoneOnly}
      size={'full'}
    >
      <Stack
        style={
          phoneOnly
            ? { padding: '0' }
            : { padding: '0', margin: '0px !important' }
        }
      >
        <JobHeader
          displayStatus={displayLabels()}
          handleCloseClick={() => onCancelPress(job)}
          handlePublishClick={togglePublish}
          isJobPublished={jobPublished}
          job={job}
          jobStatus={jobStatus}
        />
      </Stack>

      <Stack vertical gap={0}>
        <Tabs
          minWidth={phoneOnly ? '50%' : '200px'}
          selected={tab}
          showTabOnMobile={true}
          tabs={TABS}
          wrapperStyle={{
            marginLeft: phoneOnly ? '0px' : '38px',
            padding: '0px',
          }}
          onSelect={setTab}
        />
      </Stack>
      <Stack
        vertical
        gap={24}
        style={phoneOnly ? { padding: '0 14px' } : { marginTop: '20px' }}
      >
        {job.postedAt &&
          !job.published &&
          jobStatus !== JobStatus.COMPLETED && (
            <div style={{ width: '100% ' }}>
              <Alert
                description={`This job will be published to GravyWorkers on ${formatISO(
                  job.postedAt,
                  "EEE, MMM do 'at' h:mmbbb",
                )}`}
                icon={faClock}
                status="neutral"
                title="Job scheduled to be posted"
              />
            </div>
          )}

        <div style={{ width: '100%' }}>
          {tab === 0 && workerDetails()}
          {tab === 1 && jobDetails()}
          {tab === 2 && jobCustomerId && (
            <Stack
              vertical
              gap={10}
              style={phoneOnly ? { padding: '0px' } : { padding: '0px 20px' }}
            >
              <PreferredWorkers
                strictSkill
                customerId={jobCustomerId}
                initialSkillId={jobSkillId ? Number(jobSkillId) : undefined}
              />
            </Stack>
          )}
        </div>
      </Stack>
      {chatIntercomIcon && (!isIOS() || (isIOS() && ChatIntercomIconIOS)) && (
        <AgencyChat />
      )}
    </Page>
  );
};

export default JobRevamp;
